import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import _ from 'lodash';
import { IDataChart } from '@ts/ui-models';
import { generateColorShades } from '@utils/chart/chart.utils';
import { Grid, Typography } from '@ts/storybook';
import { makeStyles } from '@mui/styles';
import { useTheme, Theme } from '@mui/material';
import MainCard from './MainCard';
import { List, ListItemButton, ListItemText, Stack, Box, Skeleton } from '@mui/material';
import { IAppProps } from '@interfaces/App.interface';
import { useTranslation } from 'react-i18next';

interface PieChartProps extends IAppProps {
  title?: string;
  dataChart?: IDataChart;
  loading: boolean;
  isMock?: boolean;
}

// Chart options
const pieChartOptions: ApexOptions = {
  chart: {
    height: 350,
    type: 'donut',
    animations: {
      enabled: true,
      //easing: 'easeinout',
      speed: 800,
      animateGradually: {
        enabled: true,
        delay: 1500,
      },
      dynamicAnimation: {
        enabled: true,
        speed: 700,
      },
    },
  },
  plotOptions: {
    pie: {
      startAngle: -90,
      endAngle: 270,
      donut: {
        labels: {
          show: true,
          name: {
            formatter: val => {
              return 'Severity ' + val;
            },
          },
        },
      },
    },
  },
  dataLabels: {
    enabled: true,
    formatter: (val: number, opts) => {
      return opts.w.globals.labels[opts.seriesIndex] + ': ' + Math.round(val) + '%';
    },
    style: {
      fontSize: '14px',
      fontWeight: 'bold',
    },
    dropShadow: {
      enabled: true,
      color: '#000',
      opacity: 1,
    },
  },
  fill: {
    //type: 'gradient',
  },
  legend: {
    show: false,
    /* formatter: function (val, opts) {
      return val + ' - ' + opts.w.globals.series[opts.seriesIndex];
    }, */
  },
  tooltip: {
    enabled: false,
  },
};

const mockQuantityData = (): IDataChart => {
  return {
    labels: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
    series: [10, 3, 30, 2, 3, 4, 8, 0, 2, 0, 2, 5, 5, 3, 2],
  };
};

const useStyles = makeStyles<Theme, { theme: Theme }>(() => ({
  positionRotatedText: { position: 'absolute', zIndex: 1, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
  rotatedText: props => ({
    color: props.theme.palette.text.disabled,
    textAlign: 'center',
    transform: 'rotate(-45deg)', // Elige el ángulo de rotación deseado (en grados)
  }),
  noData: {
    backgroundColor: '#c4bfbf29',
  },
}));

const PieChart = (props: PieChartProps) => {
  const { t } = useTranslation();

  //#region Styles
  const theme: Theme = useTheme();
  const classesStyle = useStyles({ theme });
  const warning = theme.palette.warning;
  //#endregion

  const [series, setSeries] = useState(props.isMock ? mockQuantityData().series : props.dataChart?.series || []);
  const [options, setOptions] = useState<ApexOptions>(pieChartOptions);

  //#region Functions
  const fillOptionsChart = (prevState: any) => {
    return {
      ...prevState,
      colors: generateColorShades('#f6ff00', '#ff1500', 15),
      labels: props.dataChart?.labels,
    };
  };

  const getData = () => {
    // Verificar si series es una lista de objetos
    if (Array.isArray(series) && typeof series[0] === 'number') return series;
    else if (Array.isArray(series) && typeof series[0] === 'object') return series[0].data;
  };

  const checkEmptyData = () => {
    if (props.isMock) return false;
    return !getData() || getData()?.length === 0;
  };
  //#endregion

  useEffect(() => {
    setOptions(prevState => fillOptionsChart(prevState));
    setSeries(props.isMock ? mockQuantityData().series : props.dataChart?.series || []);
  }, [props.dataChart]);

  if (props.loading)
    return (
      <MainCard contentSX={{ p: 2.25 }}>
        <Stack spacing={0.5}>
          <Grid container alignItems="center">
            <Grid size={{ xs: 7 }}>
              <Skeleton variant="rounded" height={32} />
            </Grid>
            <Grid size={{ xs: 1 }} />
            <Grid size={{ xs: 4 }}>
              <Skeleton variant="rounded" height={32} />
            </Grid>
          </Grid>
        </Stack>
        <Box sx={{ pt: 2.75 }}>
          <Skeleton variant="rounded" height={420} />
        </Box>
      </MainCard>
    );

  return (
    <MainCard contentSX={{ p: 2.25 }} sx={{ height: '100%' }}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid>{props.title && <Typography variant="h5" fontWeight="500" color="primary" text={props.title} />}</Grid>
        <Grid>
          <Typography
            variant="h6"
            fontWeight="500"
            textAlign="end"
            text={['Total', ': ', _.sum(props.isMock ? mockQuantityData().series : props.dataChart?.series)]}
          />
        </Grid>
      </Grid>
      {props.isMock && (
        <List>
          <ListItemButton divider>
            <ListItemText primary="Acknowledged" />
            <Typography variant="h6" text="34/45" />
          </ListItemButton>
          <ListItemButton divider>
            <ListItemText primary="Solved" />
            <Typography variant="h6" text="32/33" />
          </ListItemButton>
          <ListItemButton>
            <ListItemText primary="Closed" />
            <Typography variant="h6" text="17/32" />
          </ListItemButton>
        </List>
      )}
      <div style={{ position: 'relative' }} className={checkEmptyData() ? classesStyle.noData : ''}>
        <div style={{ minWidth: '100%', minHeight: props.isMock ? '342px' : '455px', paddingTop: '10px' }}>
          {checkEmptyData() ? (
            <div className={classesStyle.positionRotatedText}>
              <Typography variant="h3" className={classesStyle.rotatedText} text="NO DATA" />
            </div>
          ) : null}
          <ReactApexChart
            options={options}
            series={series}
            // @ts-ignore
            type={pieChartOptions.chart?.type}
            height={pieChartOptions.chart?.height}
          />
        </div>
      </div>
    </MainCard>
  );
};

export default PieChart;
